import React, { useState, useEffect } from 'react'
import { loadCaptchaEnginge, LoadCanvasTemplate } from 'react-simple-captcha'
import { LoadCanvasTemplateNoReload, validateCaptcha } from 'react-simple-captcha'
import { Box } from '@mui/material'
import { Alert } from '@mui/material'

import { feedback } from '../../core/feedback'
import { TextInput } from '../text-input'
import { ThemeButton } from '../theme-button'

const DEFAULT_CHAR_LENGTH = 6

/**
 * @typedef {object} CaptchaInputProps
 * @property {number} [charLength=6] - Número de caracteres que tendrá el captcha.
 * @property {'row' | 'column'} [layout='column'] - Dirección del layout (horizontal o vertical).
 * @property {boolean} [isReloadable=true] - Si es true, muestra un botón para recargar el captcha.
 * @property {function(boolean): void} [onSubmit] - Callback que se ejecuta al verificar el captcha, recibe true si es correcto, false si no.
 */

/**
 * Un componente de captcha visual para verificación de usuarios humanos.
 *
 * Utiliza la librería react-simple-captcha para generar y validar un captcha alfanumérico.
 * El usuario debe introducir los caracteres mostrados en la imagen y hacer clic en verificar.
 * Una vez validado correctamente, muestra un mensaje de éxito y deja de solicitar verificación.
 *
 * @component
 * @example
 * const MyForm = () => {
 *   const handleCaptchaVerified = (isValid) => {
 *     if (isValid) {
 *       console.log('Captcha verificado correctamente');
 *       // Habilitar el envío del formulario
 *     } else {
 *       console.log('Captcha incorrecto');
 *     }
 *   };
 *
 *   return (
 *     <CaptchaInput
 *       charLength={6}
 *       layout="column"
 *       isReloadable={true}
 *       onSubmit={handleCaptchaVerified}
 *     />
 *   );
 * };
 *
 * @param {CaptchaInputProps} props - Las props del componente CaptchaInput.
 */
export const CaptchaInput = ({
  charLength = DEFAULT_CHAR_LENGTH,
  layout = 'column',
  isReloadable = true,
  onSubmit = () => {},
}) => {
  const [value, setValue] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => {
    if (!confirmed) loadCaptchaEnginge(charLength)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    if (validateCaptcha(value) === true) {
      setConfirmed(true)
      onSubmit(true)
    } else {
      loadCaptchaEnginge(charLength)
      feedback('error', 'El captcha introducido es incorrecto')
      setValue('')
      onSubmit(false)
    }
  }

  const handleChange = (e) => {
    setValue(e.target.value)
  }

  return (
    <Box mt={1} mb={1} display="flex" alignItems="center" flexDirection={layout}>
      {!confirmed ? (
        <>
          <Box mt={1} flex={1} display="flex">
            {isReloadable ? <LoadCanvasTemplate /> : <LoadCanvasTemplateNoReload />}
          </Box>
          <Box display="flex">
            <Box maxWidth={200} mr={1}>
              <TextInput name="captcha_input" onChange={handleChange} value={value} />
            </Box>
            <ThemeButton onClick={handleSubmit}>Verificar</ThemeButton>
          </Box>
        </>
      ) : (
        <Alert severity="success">Código de confirmación correcto.</Alert>
      )}
    </Box>
  )
}
